import { unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import { css, html, type TemplateResult } from 'lit';
import * as smd from 'streaming-markdown';

import type { IMarkdownRenderer } from './types';

/**
 * CSS styles for streaming markdown output.
 * Matches BlockSuite styling for visual consistency.
 */
export const markedRendererStyles = css`
  .streaming-markdown-content {
    font-family: var(--streamlain-font-family);
    line-height: var(--streamlain-line-height);
    color: ${unsafeCSSVarV2('text/primary')};
    font-weight: 400;
  }

  .streaming-markdown-content h1,
  .streaming-markdown-content h2,
  .streaming-markdown-content h3,
  .streaming-markdown-content h4,
  .streaming-markdown-content h5,
  .streaming-markdown-content h6 {
    margin-top: 1em;
    margin-bottom: 0.5em;
    font-weight: 600;
  }

  .streaming-markdown-content h1 {
    font-size: calc(var(--streamlain-font-h-1) - 2px);
  }
  .streaming-markdown-content h2 {
    font-size: calc(var(--streamlain-font-h-2) - 2px);
  }
  .streaming-markdown-content h3 {
    font-size: calc(var(--streamlain-font-h-3) - 2px);
  }
  .streaming-markdown-content h4 {
    font-size: calc(var(--streamlain-font-h-4) - 2px);
  }
  .streaming-markdown-content h5 {
    font-size: calc(var(--streamlain-font-h-5) - 2px);
  }
  .streaming-markdown-content h6 {
    font-size: calc(var(--streamlain-font-h-6) - 2px);
  }

  .streaming-markdown-content p {
    margin: 0.5em 0;
    line-height: 22px;
  }

  .streaming-markdown-content ul,
  .streaming-markdown-content ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }

  .streaming-markdown-content li {
    margin: 0.25em 0;
  }

  .streaming-markdown-content code {
    font-family: var(--streamlain-font-code-family);
    font-size: 0.9em;
    background: ${unsafeCSSVarV2('layer/background/codeBlock')};
    padding: 0.2em 0.4em;
    border-radius: 4px;
  }

  .streaming-markdown-content pre {
    background: ${unsafeCSSVarV2('layer/background/codeBlock')};
    padding: 1em;
    border-radius: 8px;
    overflow-x: auto;
    overflow-y: hidden;
    margin: 0.5em 0;
  }

  .streaming-markdown-content pre code {
    background: none;
    padding: 0;
  }

  .streaming-markdown-content blockquote {
    border-left: 3px solid ${unsafeCSSVarV2('text/secondary')};
    margin: 0.5em 0;
    padding-left: 1em;
    color: ${unsafeCSSVarV2('text/secondary')};
  }

  .streaming-markdown-content a {
    color: ${unsafeCSSVarV2('text/link')};
    text-decoration: none;
  }

  .streaming-markdown-content a:hover {
    text-decoration: underline;
  }

  .streaming-markdown-content hr {
    border: none;
    border-top: 1px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
    margin: 1em 0;
  }

  .streaming-markdown-content table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.5em 0;
  }

  .streaming-markdown-content th,
  .streaming-markdown-content td {
    border: 1px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
    padding: 0.5em;
    text-align: left;
  }

  .streaming-markdown-content th {
    background: ${unsafeCSSVarV2('layer/background/secondary')};
    font-weight: 600;
  }
`;

/**
 * Streaming markdown renderer using streaming-markdown library.
 * Optimized for real-time streaming - handles incomplete markdown gracefully.
 *
 * Key difference from marked: This library is designed for incremental updates,
 * adding new elements to the DOM without modifying existing ones.
 *
 * IMPORTANT: The container is created ONCE and reused. We use a stable container
 * reference to avoid race conditions with Lit's render cycle.
 */
export class MarkedStreamingRenderer implements IMarkdownRenderer {
  private _parser: ReturnType<typeof smd.parser> | null = null;
  private _container: HTMLDivElement | null = null;
  private _lastContent = '';

  render(markdown: string): TemplateResult {
    // Create container once and reuse it
    if (!this._container) {
      this._container = document.createElement('div');
      this._container.className = 'streaming-markdown-content';
    }

    // Update content synchronously - no RAF needed since we own the container
    this._updateContent(markdown);

    // Return the same container element wrapped in lit template
    return html`${this._container}`;
  }

  private _updateContent(markdown: string): void {
    if (!this._container) return;

    // If content changed significantly or parser doesn't exist, reset
    if (!this._parser || !markdown.startsWith(this._lastContent)) {
      this._reset(this._container);
    }

    // Get only the new content to write
    const newContent = markdown.slice(this._lastContent.length);
    if (newContent && this._parser) {
      smd.parser_write(this._parser, newContent);
    }

    this._lastContent = markdown;
  }

  private _reset(container: HTMLElement): void {
    // Clear container and create new parser
    container.innerHTML = '';
    const renderer = smd.default_renderer(container);
    this._parser = smd.parser(renderer);
    this._lastContent = '';
  }

  dispose(): void {
    if (this._parser) {
      smd.parser_end(this._parser);
      this._parser = null;
    }
    this._container = null;
    this._lastContent = '';
  }
}
